class CompareToggle extends HTMLElement {
  static MAX = 6;

  connectedCallback() {
    this._products = this._loadProducts();
    this._render();

    const wasActive = sessionStorage.getItem('compare_active') === 'true';
    if (wasActive) this._activate(false);

    this._injectBar();
    this._updateBar();

    // Delegate checkbox changes — survives grid re-renders
    document.addEventListener('change', (e) => {
      const checkbox = e.target.closest('input[data-compare-check]');
      if (!checkbox || !document.body.classList.contains('compare-mode')) return;
      this._onCheckboxChange(checkbox);
    });

    document.addEventListener('compare:grid-updated', () => this._syncCardStates());

    // Remove from comparison inside the dialog
    document.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-compare-remove]');
      if (!removeBtn) return;
      const id = String(removeBtn.dataset.compareRemove);
      this._products = this._products.filter(p => p.id !== id);
      this._saveProducts();
      this._syncCardStates();
      this._updateBar();
      const dialog = document.getElementById('compare-dialog');
      if (!dialog?.hasAttribute('open')) return;
      if (this._products.length < 2) {
        this._closeDialog(dialog);
      } else {
        this._openModal();
      }
    });

    // Wire dialog controls after DOM is ready
    requestAnimationFrame(() => {
      const dialog = document.getElementById('compare-dialog');
      const closeBtn = document.getElementById('compare-dialog-close');
      closeBtn?.addEventListener('click', () => this._closeDialog(dialog));
      dialog?.addEventListener('click', (e) => {
        if (e.target === e.currentTarget) this._closeDialog(e.currentTarget);
      });
    });
  }

  // The <dialog> API is missing below iOS 15.4 — showModal() would throw and
  // the popup silently never appears. Fall back to the open attribute plus
  // CSS (.compare-dialog--no-native) that pins the dialog as a fixed sheet.
  _showDialog(dialog) {
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.classList.add('compare-dialog--no-native');
      dialog.setAttribute('open', '');
    }
  }

  _closeDialog(dialog) {
    if (!dialog) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
  }

  _render() {
    const isOn = sessionStorage.getItem('compare_active') === 'true';
    const label = this.dataset.label || 'Compare';
    this.innerHTML = `
      <span class="compare-toggle__label">${label}</span>
      <button
        class="compare-toggle__switch"
        role="switch"
        aria-checked="${isOn}"
        type="button"
        aria-label="${label}"
      ><span class="compare-toggle__thumb"></span></button>
    `;
    this.querySelector('.compare-toggle__switch').addEventListener('click', () => this._onToggle());
  }

  _onToggle() {
    const btn = this.querySelector('.compare-toggle__switch');
    const newState = btn.getAttribute('aria-checked') !== 'true';
    btn.setAttribute('aria-checked', String(newState));
    sessionStorage.setItem('compare_active', String(newState));
    newState ? this._activate(true) : this._deactivate();
  }

  _activate(updateBar) {
    document.body.classList.add('compare-mode');
    this._syncCardStates();
    if (updateBar) this._updateBar();
  }

  _deactivate() {
    document.body.classList.remove('compare-mode');
    this._products = [];
    this._saveProducts();
    document.querySelectorAll('[data-compare-id]').forEach(c => c.classList.remove('compare-selected'));
    document.querySelectorAll('input[data-compare-check]').forEach(cb => { cb.checked = false; });
    this._updateBar();
    this._closeDialog(document.getElementById('compare-dialog'));
  }

  _onCheckboxChange(checkbox) {
    const id = String(checkbox.dataset.compareCheck);
    const card = document.querySelector(`[data-compare-id="${id}"]`);
    const idx = this._products.findIndex(p => p.id === id);

    if (checkbox.checked) {
      if (idx > -1) return;
      if (this._products.length >= CompareToggle.MAX) {
        checkbox.checked = false;
        return;
      }
      this._products.push({
        id,
        title: card?.dataset.compareTitle || '',
        image: card?.dataset.compareImage || '',
        url: card?.dataset.compareUrl || '',
      });
      card?.classList.add('compare-selected');
    } else {
      if (idx > -1) this._products.splice(idx, 1);
      card?.classList.remove('compare-selected');
    }

    this._saveProducts();
    this._updateBar();
  }

  _loadProducts() {
    try { return JSON.parse(sessionStorage.getItem('compare_products') || '[]'); }
    catch { return []; }
  }

  _saveProducts() {
    sessionStorage.setItem('compare_products', JSON.stringify(this._products));
  }

  _injectBar() {
    if (document.getElementById('compare-bar')) return;

    const btnLabel = this.dataset.compareBtn || 'Compare';
    const clearLabel = this.dataset.clear || 'Clear all';

    const bar = document.createElement('div');
    bar.id = 'compare-bar';
    bar.className = 'compare-bar';
    bar.hidden = true;
    bar.innerHTML = `
      <div class="compare-bar__inner page-width">
        <div class="compare-bar__products" id="compare-bar-products"></div>
        <div class="compare-bar__actions">
          <button class="button button--primary compare-bar__btn" id="compare-bar-btn" type="button">${btnLabel}</button>
          <button class="compare-bar__clear" type="button">${clearLabel}</button>
        </div>
      </div>
    `;
    document.body.appendChild(bar);

    bar.querySelector('.compare-bar__btn').addEventListener('click', () => this._openModal());
    bar.querySelector('.compare-bar__clear').addEventListener('click', () => this._clearAll());
  }

  _updateBar() {
    const bar = document.getElementById('compare-bar');
    if (!bar) return;

    bar.hidden = !document.body.classList.contains('compare-mode');

    const container = document.getElementById('compare-bar-products');
    const slots = [];
    for (let i = 0; i < CompareToggle.MAX; i++) {
      const p = this._products[i];
      if (p) {
        slots.push(`
          <div class="compare-bar__item">
            <button class="compare-bar__remove" type="button" data-id="${p.id}" aria-label="Remove">&times;</button>
            <img src="${p.image}" alt="${p.title}" title="${p.title} — ${location.hostname}" class="compare-bar__img" width="60" height="60" loading="lazy">
            <p class="compare-bar__name">${p.title}</p>
          </div>
        `);
      } else {
        slots.push(`
          <div class="compare-bar__item compare-bar__item--empty" aria-hidden="true">
            <div class="compare-bar__skeleton"></div>
          </div>
        `);
      }
    }
    container.innerHTML = slots.join('');

    container.querySelectorAll('.compare-bar__remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = e.currentTarget.dataset.id;
        this._products = this._products.filter(p => p.id !== id);
        this._saveProducts();
        this._syncCardStates();
        this._updateBar();
      });
    });

    const compareBtn = document.getElementById('compare-bar-btn');
    if (compareBtn) compareBtn.disabled = this._products.length < 2;
  }

  _syncCardStates() {
    const selected = new Set(this._products.map(p => p.id));
    document.querySelectorAll('[data-compare-id]').forEach(card => {
      card.classList.toggle('compare-selected', selected.has(String(card.dataset.compareId)));
    });
    document.querySelectorAll('input[data-compare-check]').forEach(cb => {
      cb.checked = selected.has(String(cb.dataset.compareCheck));
    });
  }

  _clearAll() {
    this._products = [];
    this._saveProducts();
    this._syncCardStates();
    this._updateBar();
    this._closeDialog(document.getElementById('compare-dialog'));
  }

  async _openModal() {
    const dialog = document.getElementById('compare-dialog');
    const table = document.getElementById('compare-table');
    if (!dialog || !table || this._products.length < 2) return;

    // Remove previous product columns and reveal every row before repopulating
    table.querySelectorAll('.compare-table__product-col').forEach(el => el.remove());
    const rows = Array.from(table.querySelectorAll('tr[data-compare-row]'));
    rows.forEach(row => { row.hidden = false; });

    const loading = document.getElementById('compare-dialog-loading');
    if (loading) loading.hidden = false;

    if (!dialog.hasAttribute('open')) this._showDialog(dialog);

    // Fetch all products in parallel
    const results = await Promise.all(
      this._products.map(p => this._fetchProductData(p.url))
    );

    // Populate each table row with a cell per product, tracking which rows
    // actually received data from at least one product.
    const rowHasData = new Map(rows.map(row => [row, false]));

    results.forEach((colData, i) => {
      const productId = this._products[i].id;
      rows.forEach(row => {
        const key = row.dataset.compareRow;
        const td = document.createElement('td');
        td.className = 'compare-table__product-col';
        td.dataset.compareProductId = productId;

        const fieldEl = colData?.querySelector(`[data-compare-col="${key}"]`);
        if (fieldEl) {
          td.innerHTML = fieldEl.innerHTML;
          if (fieldEl.dataset.compareEmpty !== 'true') rowHasData.set(row, true);
        } else {
          td.innerHTML = '<span>&mdash;</span>';
        }
        row.appendChild(td);
      });
    });

    // Hide rows where none of the compared products has a value. Skipped when
    // every fetch failed, so the table still renders instead of an empty dialog.
    if (results.some(Boolean)) {
      rowHasData.forEach((hasData, row) => { row.hidden = !hasData; });
    }

    if (loading) loading.hidden = true;
  }

  async _fetchProductData(productUrl) {
    try {
      const sep = productUrl.includes('?') ? '&' : '?';
      const res = await fetch(`${productUrl}${sep}sections=product-compare-data`);
      if (!res.ok) return null;
      const json = await res.json();
      const html = json['product-compare-data'];
      if (!html) return null;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.querySelector('.compare-product-data');
    } catch {
      return null;
    }
  }
}

customElements.define('compare-toggle', CompareToggle);
